import { DataDogConfigNew } from 'utils/dataDog';
import { isLocalHost } from 'utils/url';
import { CUSTOM_ACTION_NAMES } from './constants';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { getUserIdentifier } from 'utils/id';

/**
 * Get the common DD log properties.
 * @returns An object with common properties we want to include in every DD log.
 */
const getCommonLogProps = () => ({
  referrer: window[CHATUI_WINDOW_PATH].getReferrer(),
  ...getUserIdentifier(),
  conversationId: window[CHATUI_WINDOW_PATH].getConversationId(),
});

/**
 * Used to log custom errors or custom actions to DataDog using global `window.DD_RUM` object.
 * If the `DD_RUM` object already exists, use it. If not, initialize with configurations for ChatUI.
 * For localhost, just log messages in console.
 */
export const DataDogLogger = {
  context: {},
  init: function init(serviceName: string) {
    this.context = { serviceName };

    const isAlreadyInitializedWithConfig = window?.DD_RUM && window.DD_RUM?.getInitConfiguration();
    if (isLocalHost() || isAlreadyInitializedWithConfig) return;
    else DataDogConfigNew(serviceName);
  },

  /**
   * Add a custom DD error.
   *
   * @see getCommonLogProps to seee which props are automatically added to all DD logs.
   *
   * @param error An instance of an `Error` object, an error message string, or a custom error object.
   * @param context Additional information to add to the DD context object.
   * @returns
   */
  error: function error(error: Error | string, context = {} as any) {
    // This data object is equivalent to the @context object in DD
    const data = { ...this.context, ...context, ...getCommonLogProps() };
    if (isLocalHost()) {
      console.log('%c🐶 DataDog error:\n', 'color: #c75b5b', { error, context, data });
      return;
    }

    if (window?.DD_RUM) {
      if (typeof error === 'object') window.DD_RUM.addError(error, data);
      if (typeof error === 'string') window.DD_RUM.addError(new Error(error), data);
    }
  },

  /**
   * Add a custom DD action
   *
   * @see getCommonLogProps to seee which props are automatically added to all DD logs.
   *
   * @param name The name of the custom action.
   * @param context Object containing additional information to pass to the DD `@context` object.
   * @param type Optional property to specify the DD `@context.type`. Defaults to `custom`.
   * @returns
   */
  action: function action(
    name: typeof CUSTOM_ACTION_NAMES | string,
    context = {} as any,
    type?: string,
  ) {
    // This data object is equivalent to the @context object in DD
    const data = {
      ...this.context,
      ...context,
      type: type || 'custom',
      ...getCommonLogProps(),
    };
    if (isLocalHost()) {
      console.log('%c🐶 DataDog action:\n', 'color: #5b8ec7', { name, context, data });
      return;
    }

    if (window?.DD_RUM) window.DD_RUM.addAction(name, data);
  },
};

export default DataDogLogger;
